import styled from '@emotion/styled';

import { calculateOverflow } from '@hotjar/webapp/lib/calculate-overflow';
import { Overflow, Label } from '@hotjar/labels';
import { Avatar } from '@hotjar/webapp/components/Avatar';
import { Tooltip, tokens } from '@hotjar/hotsauce';
import { AppliedLabel, Comment } from '../../domains/highlights';
import { useTranslation } from '@hotjar/translations';

const List = styled.ul<{ slots: number }>`
  display: flex;
  justify-content: center;
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const Item = styled.li<{ size: number }>`
  margin: 0;
  padding: 0;
  margin-left: -${({ size }) => size / 3}px;
  &:first-of-type {
    margin-left: 0;
  }
`;

const CommentContentWrapper = styled.div`
  ${tokens.truncateText()}
  white-space: unset;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const itemSizeInPixels = {
  m: 16,
  l: 24,
} as const;

const TYPE_LABEL = 'LABEL';
const TYPE_AVATAR = 'AVATAR';

interface LabelsCommentsListProps {
  className?: string;
  labels?: Array<AppliedLabel>;
  comments?: Array<Comment>;
  size: 'm' | 'l';
  slotsNumber: number;
}

export function LabelsCommentsList({
  className,
  labels = [],
  comments = [],
  size,
  slotsNumber,
}: LabelsCommentsListProps): JSX.Element {
  const { t } = useTranslation();
  const items = [
    ...comments.map((item) => ({ item, type: TYPE_AVATAR })),
    ...labels.map((item) => ({ item, type: TYPE_LABEL })),
  ];

  const [visibleItems, overflowCount] = calculateOverflow(items, slotsNumber);
  const overflowItems = items.slice(slotsNumber - 1);

  const countSuffixMap: Record<string, number> = {};
  overflowItems.forEach(({ item, type }) => {
    const key = type === TYPE_LABEL ? `${(item as AppliedLabel).name} label` : 'comment';
    countSuffixMap[key] = (countSuffixMap[key] ?? 0) + 1;
  });

  const overflowTooltip = Object.entries(countSuffixMap)
    .map(([suffix, count]) => [count, `${suffix}${count > 1 ? 's' : ''}`].join(' '))
    .join('\n');

  if (visibleItems.length === 0) {
    return <span>-</span>;
  }

  return (
    <List className={className} slots={slotsNumber}>
      {visibleItems.map(({ type, item }) =>
        type == TYPE_LABEL ? (
          <Item
            key={`${type}.${(item as AppliedLabel).highlightId}.${item.id}`}
            size={itemSizeInPixels[size]}
          >
            <Label
              iconName={(item as AppliedLabel).icon}
              name={
                (item as AppliedLabel).keyName !== null
                  ? t(String((item as AppliedLabel).keyName))
                  : (item as AppliedLabel).name
              }
              size={size}
            />
          </Item>
        ) : (
          <Item key={`${type}.${item.id}`} size={itemSizeInPixels[size]}>
            <Avatar
              url={(item as Comment).userPictureUrl ?? undefined}
              name={(item as Comment).userName ?? undefined}
              size={itemSizeInPixels[size]}
              outline={true}
              tooltipContent={
                <CommentContentWrapper>
                  {((item as Comment).contentJsonEnhanced ?? []).map((token) => {
                    if (token.type !== 'mention') {
                      return token.value;
                    }

                    return <strong key={token.value.id}>{token.value.name}</strong>;
                  })}
                </CommentContentWrapper>
              }
              tooltipMaxWidth={320}
            />
          </Item>
        ),
      )}

      {overflowCount > 0 ? (
        <Tooltip text={overflowTooltip}>
          <Item key="overflow" size={itemSizeInPixels[size]}>
            <Overflow number={overflowCount} size={size} />
          </Item>
        </Tooltip>
      ) : null}
    </List>
  );
}
